// vendors
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';

// utils
import { FILES_API_URL, AWS_REGION, S3_BUCKET_FILES, AWS_KEY } from 'config';
import { ActionPermission } from 'lib/specialPermissions';

// components
import { Form, Formik } from 'formik';
import { PhotoUploader } from 'lego-dist';
import themes from 'themes';
import RendererIf from 'components/RendererIf';

// hooks
import { useRouter } from 'routes';
import useCurrentUser from 'hooks/useCurrentUser';

// query config
import { classroomQuery, putClassroomAvatarQuery } from '../queryConfig';

// styles
import {
  Overlay,
  EditBtn,
  EditSave,
  EditCancel,
  EditActions,
  ErrorMessage,
  EditSaveMobile,
  EditSaveDesktop,
  EditCancelMobile,
  ClassroomDetailsImg,
  ClassroomDetailsName,
  ClassroomDetailsEditName,
  ClassroomDetailsInfoWrapper,
  ClassroomDetailsNameCharCount,
  ClassroomDetailsEditWrapperMobile,
  ClassroomDetailsEditWrapperDesktop,
} from './styles';

interface IClassroomDetailsInfoProps {
  classroomData: any;
}

const classroomFormValidations = Yup.object().shape({
  name: Yup.string().trim().required('Required'),
});

const { putClassroomQuery } = classroomQuery;

let newClassroomName: any;

const ClassroomDetailsInfo: React.FC<IClassroomDetailsInfoProps> = ({
  classroomData,
}) => {
  const { t: translate } = useTranslation();
  const {
    currentParamsRef: {
      current: { groupId, centerId },
    },
  } = useRouter();
  const { can } = useCurrentUser();
  const canEdit = can(ActionPermission.ADD_EDIT_DELETE_CLASSROOMS);

  const [putAvatar] = useMutation(putClassroomAvatarQuery.resource);
  const [showEditClassroomName, setShowEditClassroomName] = useState(false);

  const [putClassroom, { isSuccess }]: any = useMutation(
    putClassroomQuery.resource,
  );

  /* track characters in input field(s) */
  const [chars, setChars] = useState(0);

  /* UPLOAD CLASSROOM AVATAR */
  const onUploadedClassroomAvatar = ({ s3FilePath, croppedFile }: any) => {
    const s3_files_attributes = [
      {
        file_name: s3FilePath,
        status: 'Complete',
        mime_type: croppedFile.type,
        service_urls_attributes: [
          {
            record_type: 'thumbnail_url',
            url: s3FilePath,
          },
        ],
      },
    ];
    putAvatar({
      groupId,
      centerId,
      body: {
        group: { s3_files_attributes },
      },
    });
  };

  const onSubmitForm = ({ name }: any) => {
    newClassroomName = name;
    const body = {
      group: { name },
    };
    putClassroom({ groupId, centerId, body });
  };

  useEffect(() => {
    classroomData.name = newClassroomName;
    setShowEditClassroomName(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);
  
  return (
    <ClassroomDetailsInfoWrapper>
      <ClassroomDetailsImg disable={!canEdit}>
        <PhotoUploader
          multipart
          src={classroomData?.logo}
          previewModalProps={{ headerColor: themes.colors.newKinedu }}
          withDefaultIcon={!classroomData?.name}
          defaultLetter={classroomData?.name?.charAt(0).toUpperCase()}
          onCroppedFile={({ uploadFileToS3, croppedFile }: any) => {
            uploadFileToS3(croppedFile);
          }}
          positiveButtonProps={{
            children: translate('SAVE'),
          }}
          negativeButtonProps={{
            children: translate('CANCEL'),
          }}
          onUploadedFile={({ s3FilePath, croppedFile }: any) => {
            onUploadedClassroomAvatar({ s3FilePath, croppedFile });
          }}
          s3UploaderProps={{
            apiURL: FILES_API_URL,
            awsRegion: AWS_REGION,
            bucket: S3_BUCKET_FILES,
            aws_key: AWS_KEY,
          }}
          s3path={`${classroomData?.id}/avatars/groups/${Date.now()}_${
            localStorage?.ACTIVE_CENTER_ID
          }_`}
        />
      </ClassroomDetailsImg>
      <Formik
        validateOnBlur
        validateOnChange
        onSubmit={onSubmitForm}
        initialValues={classroomData}
        initialStatus={!!classroomData}
        isInitialValid={!!classroomData}
        validationSchema={classroomFormValidations}
        render={({ setFieldValue, errors, isValid }: any) => (
          <Form>
            {showEditClassroomName ? (
              <>
                {/* MOBILE */}
                <Overlay />
                <ClassroomDetailsEditWrapperMobile>
                  <ClassroomDetailsNameCharCount>
                    {' '}
                    {chars} / 70
                  </ClassroomDetailsNameCharCount>
                  <ClassroomDetailsEditName
                    type="text"
                    value={classroomData?.name}
                    maxLength={70}
                    onChange={(event: any) => {
                      setChars(event.length);
                      setFieldValue('name', event);
                    }}
                  />
                  {errors.name ? (
                    <ErrorMessage>
                      {translate(
                        'settings:CENTER_SECTION.CLASSROOM_CONFIG.NAME_REQUIRED',
                      )}
                    </ErrorMessage>
                  ) : null}
                  {chars === 70 ? (
                    <ErrorMessage>
                      {translate(
                        'settings:CENTER_SECTION.CLASSROOM_CONFIG.MAX_CHARACTERS',
                        { max: 70 },
                      )}
                    </ErrorMessage>
                  ) : null}
                  <EditActions>
                    <EditCancelMobile
                      rounded
                      flat
                      outline
                      onClick={() => {
                        setShowEditClassroomName(false);
                        setChars(classroomData?.name.length);
                      }}
                    >
                      {translate('common:CANCEL')}
                    </EditCancelMobile>
                    <EditSaveMobile
                      type="submit"
                      disabled={!isValid}
                      rounded
                      flat
                      color={isValid ? '#75B753' : 'disabled'}
                      // onSubmit={() => handleSubmit()}
                    >
                      {translate('common:SAVE')}
                    </EditSaveMobile>
                  </EditActions>
                </ClassroomDetailsEditWrapperMobile>
                {/* DESKTOP */}
                <ClassroomDetailsEditWrapperDesktop>
                  <ClassroomDetailsNameCharCount>
                    {' '}
                    {chars} / 70
                  </ClassroomDetailsNameCharCount>
                  <ClassroomDetailsEditName
                    type="text"
                    value={classroomData?.name}
                    maxLength={70}
                    onChange={(event: any) => {
                      setChars(event.length);
                      setFieldValue('name', event);
                    }}
                  />
                  {errors.name ? (
                    <ErrorMessage>
                      {translate(
                        'settings:CENTER_SECTION.CLASSROOM_CONFIG.NAME_REQUIRED',
                      )}
                    </ErrorMessage>
                  ) : null}
                  {chars === 70 ? (
                    <ErrorMessage>
                      {translate(
                        'settings:CENTER_SECTION.CLASSROOM_CONFIG.MAX_CHARACTERS',
                        { max: 70 },
                      )}
                    </ErrorMessage>
                  ) : null}
                  <EditActions>
                    <EditCancel
                      onClick={() => {
                        setShowEditClassroomName(false);
                        setChars(classroomData?.name.length);
                      }}
                    />
                    <EditSaveDesktop
                      type="submit"
                      disabled={!isValid}
                    >
                      <EditSave />
                    </EditSaveDesktop>
                  </EditActions>
                </ClassroomDetailsEditWrapperDesktop>
              </>
            ) : (
              <>
                <ClassroomDetailsName>
                  {classroomData?.name}
                </ClassroomDetailsName>
                <RendererIf condition={canEdit}>
                  <EditBtn
                    onClick={() => {
                      setChars(classroomData?.name.length);
                      setShowEditClassroomName(!showEditClassroomName);
                    }}
                  />
                </RendererIf>
              </>
            )}
          </Form>
        )}
      />
    </ClassroomDetailsInfoWrapper>
  );
};

export default ClassroomDetailsInfo;
